import { observable, computed, action, reaction } from 'mobx'
import { has } from 'lodash'

import BaseListItem, {
  IBaseListItemUpdateOptions,
} from 'stores/base/BaseListItem'
import ThemeStore from 'stores/theme/ThemeStore'
import TabStore from 'stores/tab/TabStore'
import ModelStore from 'stores/model/ModelStore'
import Model from 'stores/model/Model'
import PersistenceStore from 'stores/persistence/PersistenceStore'
import BootstrapStore from 'stores/bootstrap/BootstrapStore'
import { IProjectSchema } from 'stores/persistence/types'

export interface IProjectCreatePayload {
  id: string
  name: string
  schemaPath: string
  recentModelIds: string[]
}

export default class Project extends BaseListItem {
  @observable id: string
  @observable name: string
  @observable schemaPath: string
  @observable recentModelIds: string[]

  constructor(project: IProjectCreatePayload) {
    super(project)

    this.id = project.id
    this.name = project.name
    this.schemaPath = project.schemaPath
    this.recentModelIds = project.recentModelIds || []

    // Set up a reaction to save the project when one of its (serialize) dependencies change
    // TODO:: Remove this reaction. Ideally, store these things in the Project itself so that BaseListItem can save to IndexedDB automatically
    reaction(
      () => [TabStore.activeTabId, ThemeStore.theme],
      () => {
        // Check if BootstrapStore is ready first, because the above things change during bootstrap, before all stores are ready
        BootstrapStore.ready &&
          PersistenceStore.save('projects', this.serialize())
      },
      { fireImmediately: true }
    )
  }

  @computed
  get recentModels(): Model[] {
    return this.recentModelIds
      .map(mId => ModelStore.get(mId))
      .filter((m): m is Model => !!m)
  }

  @action
  addRecentModel(modelId: string) {
    if (this.recentModelIds.includes(modelId)) {
      // If this model already exists in the recent models, we want to reorder it to be the first element
      // If we just remove it, the last line of this function will make sure it gets added to the top
      this.recentModelIds = this.recentModelIds.filter(mId => mId !== modelId)
    }

    if (this.recentModelIds.length === 5) {
      // If there are already 5 elements in here, remove the last element
      this.recentModelIds.pop()
    }

    this.recentModelIds.unshift(modelId)
  }

  @action
  update = (
    project: Partial<IProjectCreatePayload>,
    options: IBaseListItemUpdateOptions
  ) => {
    has(project, 'name') && (this.name = project.name!)
    has(project, 'path') && (this.schemaPath = project.schemaPath!)
    has(project, 'recentModelIds') &&
      (this.recentModelIds = project.recentModelIds!)

    super.update(project, options)
  }

  serialize(): IProjectSchema {
    // When adding things here, be sure to also watch for changes in the reaction in my constructor
    return {
      id: this.id,
      activeTabId: String(TabStore.activeTabId),
      recentModelIds: this.recentModelIds.map(mId => String(mId)), // String constructor to make sure it is not an observable
      theme: ThemeStore.theme,
    }
  }
}
